import {
  InspectorControls,
  PanelColorSettings,
  FontSizePicker,
  getColorObjectByColorValue,
} from "@wordpress/block-editor";

import {
  PanelBody,
  Button,
  BaseControl,
  SelectControl,
  __experimentalToolsPanel as ToolsPanel,
  __experimentalToolsPanelItem as ToolsPanelItem,
  __experimentalBorderBoxControl as BorderBoxControl,
} from "@wordpress/components";

import { useState, useEffect } from "@wordpress/element";
import { useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import IconsModal from "../../../components/IconsModal";
import ShadowPicker from "../../../../../Externals/Src/Gutenberg/CustomBoxShadow/ShadowComponent";
import { getEditorColorPalette } from "../../../utils/helpers";

export default function Settings({ attributes, setAttributes }) {
  const { iconOpen, iconClose, style } = attributes;

  const {
    headerActiveColor,
    activeBackground,
    headerActiveTypography,
    activeBorder,
    activeShadow,
  } = style;

  // Get editor color palette using the proper hook
  const colors = useSelect(getEditorColorPalette, []);

  const [isIconsModalOpen, setIsIconsModalOpen] = useState(false);
  const [currentIconAttribute, setCurrentIconAttribute] = useState("iconOpen");
  const [currentIcon, setCurrentIcon] = useState({
    name: iconOpen,
    type: "regular",
  });

  useEffect(() => {
    setCurrentIcon({
      name: iconOpen,
      type: "regular",
    });
  }, [iconOpen]);

  const handleChangeIcon = (iconAttribute) => {
    setCurrentIconAttribute(iconAttribute);
    setCurrentIcon({
      name: attributes[iconAttribute],
      type: "regular",
    });
    setIsIconsModalOpen(true);
  };

  const resetCollapsibleActive = () => {
    setAttributes({
      style: {
        ...style,
        activeBorder: {
          style: "solid",
          width: "0px",
          color: "#1e1e1e",
        },
        activeShadow: "",
        activeBackground: "transparent",
      },
    });
  };

  const resetCollapsibleHeaderActive = () => {
    setAttributes({
      style: {
        ...style,
        headerActiveColor: "",
        headerActiveTypography: {
          fontSize: "",
          fontWeight: "",
          textTransform: "",
        },
      },
    });
  };

  return (
    <>
      <InspectorControls>
        <PanelBody
          title={__("Icon Settings", "brandy-blocks")}
          initialOpen={true}
        >
          <div className="brandy-collapsible-icon-settings">
            <BaseControl
              label={__("Open State Icon", "brandy-blocks")}
              help={__(
                "Icon shown when the collapsible is closed",
                "brandy-blocks"
              )}
            >
              <div className="brandy-collapsible-icon-preview">
                {iconOpen && <i className={`ph ph-${iconOpen}`}></i>}
                <Button
                  variant="secondary"
                  onClick={() => handleChangeIcon("iconOpen")}
                  className="brandy-collapsible-icon-button"
                >
                  {iconOpen
                    ? __("Change Icon", "brandy-blocks")
                    : __("Add Icon", "brandy-blocks")}
                </Button>
              </div>
            </BaseControl>

            <BaseControl
              label={__("Close State Icon", "brandy-blocks")}
              help={__(
                "Icon shown when the collapsible is open",
                "brandy-blocks"
              )}
            >
              <div className="brandy-collapsible-icon-preview">
                {iconClose && <i className={`ph ph-${iconClose}`}></i>}
                <Button
                  variant="secondary"
                  onClick={() => handleChangeIcon("iconClose")}
                  className="brandy-collapsible-icon-button"
                >
                  {iconClose
                    ? __("Change Icon", "brandy-blocks")
                    : __("Add Icon", "brandy-blocks")}
                </Button>
              </div>
            </BaseControl>
          </div>
        </PanelBody>
      </InspectorControls>
      <InspectorControls group="styles">
        <ToolsPanel
          label={__("Collapsible Active", "brandy-blocks")}
          resetAll={resetCollapsibleActive}
        >
          <ToolsPanelItem
            hasValue={() => !!activeBorder}
            label={__("Border", "brandy-blocks")}
            onDeselect={() =>
              setAttributes({
                style: {
                  ...style,
                  activeBorder: {
                    style: "solid",
                    width: "1px",
                    color: "#1e1e1e",
                  },
                },
              })
            }
            isShownByDefault
          >
            <BorderBoxControl
              colors={colors}
              label={__("Border", "brandy-blocks")}
              value={activeBorder}
              onChange={(v) => {
                setAttributes({
                  style: { ...style, activeBorder: v },
                });
              }}
            />
          </ToolsPanelItem>
          <ToolsPanelItem
            hasValue={() => !!activeShadow}
            label={__("Shadow", "brandy-blocks")}
            onDeselect={() =>
              setAttributes({
                style: {
                  ...style,
                  activeShadow: "",
                },
              })
            }
            isShownByDefault
          >
            <ShadowPicker
              label={__("Shadow", "brandy-blocks")}
              shadow={activeShadow}
              onChange={(v) => {
                setAttributes({
                  style: { ...style, activeShadow: v },
                });
              }}
            />
          </ToolsPanelItem>
          <ToolsPanelItem
            hasValue={() => !!activeBackground?.color}
            label={__("Background", "brandy-blocks")}
            onDeselect={() =>
              setAttributes({
                style: { ...style, activeBackground: { color: "", slug: "" } },
              })
            }
            isShownByDefault
          >
            <div className="brandy-collapsible-active-background">
              <PanelColorSettings
                enableAlpha
                showTitle={false}
                __experimentalIsRenderedInSidebar
                colorSettings={[
                  {
                    value: activeBackground?.color,
                    onChange: (color) => {
                      //get the slug of the color
                      const colorObject = getColorObjectByColorValue(
                        colors,
                        color
                      );
                      const slug = colorObject ? colorObject.slug : "";
                      setAttributes({
                        style: {
                          ...style,
                          activeBackground: { color: color, slug: slug },
                        },
                      });
                    },
                    label: __("Background", "brandy-blocks"),
                    clearable: true,
                  },
                ]}
              />
            </div>
          </ToolsPanelItem>
        </ToolsPanel>
      </InspectorControls>
      <InspectorControls group="styles">
        <ToolsPanel
          label={__("Header Active", "brandy-blocks")}
          resetAll={resetCollapsibleHeaderActive}
        >
          <ToolsPanelItem
            hasValue={() => !!headerActiveColor?.color}
            label={__("Color", "brandy-blocks")}
            onDeselect={() =>
              setAttributes({
                style: { ...style, headerActiveColor: { color: "", slug: "" } },
              })
            }
            isShownByDefault
          >
            <div className="brandy-collapsible-header-active-color">
              <PanelColorSettings
                enableAlpha
                showTitle={false}
                __experimentalIsRenderedInSidebar
                clearable
                colorSettings={[
                  {
                    value: headerActiveColor?.color,
                    onChange: (color) => {
                      const colorObject = getColorObjectByColorValue(
                        colors,
                        color
                      );
                      const slug = colorObject ? colorObject.slug : "";
                      setAttributes({
                        style: {
                          ...style,
                          headerActiveColor: { color: color, slug: slug },
                        },
                      });
                    },
                    label: __("Color", "brandy-blocks"),
                    clearable: true,
                  },
                ]}
              />
            </div>
          </ToolsPanelItem>
          <ToolsPanelItem
            hasValue={() => !!headerActiveTypography?.fontWeight}
            label={__("Font Weight", "brandy-blocks")}
            onDeselect={() =>
              setAttributes({
                style: {
                  ...style,
                  headerActiveTypography: {
                    ...headerActiveTypography,
                    fontWeight: "",
                  },
                },
              })
            }
            isShownByDefault
          >
            <SelectControl
              label={__("Font Weight", "brandy-blocks")}
              value={headerActiveTypography?.fontWeight || ""}
              __next40pxDefaultSize={true}
              options={[
                { label: __("Default", "brandy-blocks"), value: "" },
                { label: __("Thin (100)", "brandy-blocks"), value: "100" },
                {
                  label: __("Extra Light (200)", "brandy-blocks"),
                  value: "200",
                },
                { label: __("Light (300)", "brandy-blocks"), value: "300" },
                { label: __("Regular (400)", "brandy-blocks"), value: "400" },
                { label: __("Medium (500)", "brandy-blocks"), value: "500" },
                { label: __("Semi Bold (600)", "brandy-blocks"), value: "600" },
                { label: __("Bold (700)", "brandy-blocks"), value: "700" },
                {
                  label: __("Extra Bold (800)", "brandy-blocks"),
                  value: "800",
                },
                { label: __("Black (900)", "brandy-blocks"), value: "900" },
              ]}
              onChange={(value) =>
                setAttributes({
                  style: {
                    ...style,
                    headerActiveTypography: {
                      ...headerActiveTypography,
                      fontWeight: value,
                    },
                  },
                })
              }
            />
          </ToolsPanelItem>
        </ToolsPanel>
      </InspectorControls>
      <IconsModal
        isIconsModalOpen={isIconsModalOpen}
        setIsIconsModalOpen={setIsIconsModalOpen}
        icon={currentIcon}
        attributes={attributes}
        setAttributes={(newAttrs) => {
          if (currentIconAttribute === "iconOpen") {
            setAttributes({
              iconOpen: newAttrs.name,
            });
          } else {
            setAttributes({
              iconClose: newAttrs.name,
            });
          }
        }}
        iconStyle={false}
      />
    </>
  );
}
